// Matchium i18n — Phase B scaffolding.
// Safe, additive multi-language infrastructure. Does not touch auth
// localStorage keys (crd_sport_token / crd_sport_user) or any business
// logic. Uses its own localStorage key: "matchium_lang".

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import es from "./locales/es.json";
import ca from "./locales/ca.json";
import pt from "./locales/pt.json";
import fr from "./locales/fr.json";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "ca", label: "Català", flag: "🏴󠁥󠁳󠁣󠁴󠁿" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
];

export const LANG_STORAGE_KEY = "matchium_lang";
const DEFAULT_LANG = "en";

export const SUPPORTED_LANG_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

/**
 * Normalize an arbitrary i18next language code (e.g. "es-AR", "en-US")
 * to one of the supported backend codes. Falls back to DEFAULT_LANG.
 */
export const normalizeLang = (code) => {
  const base = String(code || "").toLowerCase().split("-")[0];
  return SUPPORTED_LANG_CODES.includes(base) ? base : DEFAULT_LANG;
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      ca: { translation: ca },
      pt: { translation: pt },
      fr: { translation: fr },
    },
    fallbackLng: DEFAULT_LANG,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true, // map "es-AR" -> "es", "pt-BR" -> "pt", etc.
    interpolation: { escapeValue: false }, // React already escapes
    returnEmptyString: false,
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ["localStorage"],
    },
  });

export default i18n;

// Keep the document language attribute in sync with the active i18next locale.
// This (a) gives browser-native form validation tooltips the correct language,
// (b) improves screen-reader output and SEO, (c) costs nothing and is fully safe.
const _applyHtmlLang = (lng) => {
  if (typeof document === "undefined") return;
  const code = normalizeLang(lng);
  if (document.documentElement.getAttribute("lang") !== code) {
    document.documentElement.setAttribute("lang", code);
  }
};
_applyHtmlLang(i18n.language);
i18n.on("languageChanged", _applyHtmlLang);
